/**
 * Thème Maison : petites aides pour le lot « Preuve sociale ».
 *
 * - <mx-proof-carousel> : ajoute les flèches du clavier (gauche, droite, début, fin)
 *   au carrousel d'Horizon (<slideshow-component>) qu'il entoure.
 * - <mx-before-after-slider> : nomme le curseur avant/après et le laisse glisser
 *   sans faire défiler le carrousel.
 * - <mx-strip-pause> : bouton pause de la bande de logos défilante.
 * - <mx-reveal-list> : bouton « Voir plus d'avis » qui affiche la suite par paquets.
 * - <mx-autoplay-video> : vidéo courte muette, en pause si la personne
 *   préfère réduire les animations ou si la vidéo n'est pas à l'écran.
 */

const TYPING_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];

class MxProofCarousel extends HTMLElement {
  connectedCallback() {
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /** @returns {any} */
  get slideshow() {
    return this.querySelector('slideshow-component');
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || TYPING_TAGS.includes(target.tagName) || target.isContentEditable) return;
    if (target.closest('[data-mx-no-drag]')) return;

    const slideshow = this.slideshow;
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const count = slideshow.slides?.length ?? 0;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        slideshow.previous();
        break;
      case 'ArrowRight':
        event.preventDefault();
        slideshow.next();
        break;
      case 'Home':
        event.preventDefault();
        slideshow.select(0);
        break;
      case 'End':
        if (count > 0) {
          event.preventDefault();
          slideshow.select(count - 1);
        }
        break;
      default:
        break;
    }
  };
}

/**
 * Enveloppe du curseur avant/après d'Horizon (<comparison-slider-component>).
 * Donne un nom clair au curseur pour les lecteurs d'écran et empêche le carrousel
 * de capter le glissement de la souris.
 */
class MxBeforeAfterSlider extends HTMLElement {
  connectedCallback() {
    this.addEventListener('mousedown', this.#onMouseDown);
    const label = this.getAttribute('label');
    const input = this.querySelector('input[type="range"]');
    if (label && input) input.setAttribute('aria-label', label);
  }

  disconnectedCallback() {
    this.removeEventListener('mousedown', this.#onMouseDown);
  }

  /** @param {MouseEvent} event */
  #onMouseDown = (event) => {
    // Le curseur garde son glissement natif ; le carrousel ne le voit pas.
    event.stopPropagation();
  };
}

/** Bouton pause de la bande de logos défilante. */
class MxStripPause extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = () => {
    const strip = this.closest('.mx-strip');
    if (!strip || !this.button) return;
    const paused = this.button.getAttribute('aria-pressed') === 'true';
    this.button.setAttribute('aria-pressed', String(!paused));
    strip.toggleAttribute('data-paused', !paused);
  };
}

class MxRevealList extends HTMLElement {
  connectedCallback() {
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-mx-reveal-button]'));
    this.status = this.querySelector('[data-mx-reveal-status]');
    this.batch = Number(this.getAttribute('batch')) || 6;

    this.button?.addEventListener('click', this.#onClick);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.#updateButton();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  get hiddenItems() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.mx-is-extra:not([data-revealed])')));
  }

  #updateButton() {
    if (!this.button) return;
    this.button.hidden = this.hiddenItems.length === 0;
  }

  #onClick = () => {
    const next = this.hiddenItems.slice(0, this.batch);
    if (!next.length) return;

    for (const item of next) item.setAttribute('data-revealed', '');

    const first = next[0];
    if (first) {
      first.setAttribute('tabindex', '-1');
      first.focus({ preventScroll: false });
    }

    if (this.status) {
      const template = this.getAttribute('status-template') || '[n] éléments de plus affichés.';
      this.status.textContent = template.replace('[n]', String(next.length));
    }

    this.#updateButton();
  };

  /** Dans l'éditeur : un avis caché choisi dans la colonne de gauche s'affiche. */
  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element) || !this.contains(target)) return;

    const item = target.closest('.mx-is-extra');
    if (item) {
      item.setAttribute('data-revealed', '');
      this.#updateButton();
    }
  };
}

class MxAutoplayVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.reducedMotion.matches) {
      this.video.pause();
      this.video.removeAttribute('autoplay');
      this.video.controls = true;
      return;
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!this.video) return;
          if (entry.isIntersecting) {
            this.video.play().catch(() => {
              // Lecture refusée par le navigateur : on montre les commandes.
              if (this.video) this.video.controls = true;
            });
          } else {
            this.video.pause();
          }
        }
      });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('mx-proof-carousel')) customElements.define('mx-proof-carousel', MxProofCarousel);
if (!customElements.get('mx-before-after-slider')) customElements.define('mx-before-after-slider', MxBeforeAfterSlider);
if (!customElements.get('mx-strip-pause')) customElements.define('mx-strip-pause', MxStripPause);
if (!customElements.get('mx-reveal-list')) customElements.define('mx-reveal-list', MxRevealList);
if (!customElements.get('mx-autoplay-video')) customElements.define('mx-autoplay-video', MxAutoplayVideo);
